import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  CheckCircle, Upload, Lock, Star, Loader2,
  ShoppingCart, RefreshCw, AlertCircle,
  CreditCard, QrCode, Shield, Clock, Zap
} from 'lucide-react';
import Layout from '../../components/Layout';
import api from '../../utils/api';

// ── Planos (R$ 39,99/mês com descontos progressivos) ─────────
const PLANOS = [
  {
    id: '30_dias', dias: 30, label: 'Mensal',
    preco: 49.99,
    descricao: '1 mês de acesso completo',
    desconto: null,
    popular: false,
  },
  {
    id: '90_dias', dias: 90, label: 'Trimestral',
    preco: 134.97,   // 39,99 × 3 = 119,97 → 10% off
    preco_original: 149.97,
    descricao: '3 meses · economia de R$ 15,00',
    desconto: '10% off',
    popular: true,
  },
  {
    id: '180_dias', dias: 180, label: 'Semestral',
    preco: 269.95,  // 39,99 × 6 = 239,94 → 10% off
    preco_original: 299.94,
    descricao: '6 meses · economia de R$ 29,99',
    desconto: '10% off',
    popular: false,
  },
  {
    id: '365_dias', dias: 365, label: 'Anual',
    preco: 419.92,  // 39,99 × 12 = 479,88 → 30% off
    preco_original: 599.88,
    descricao: '12 meses · economia de R$ 179,96',
    desconto: '30% off',
    popular: false,
  },
];

const PIX_CHAVE       = '30.856.665/0001-62';
const PIX_BENEFICIARIO = 'SANTOS E BARROSO LTDA';

// ── Tela de retorno do MercadoPago ────────────────────────────
function TelaRetorno({ status, paymentId, onVoltar, onVerificar, verificando }) {
  const navigate = useNavigate();

  if (status === 'approved') {
    return (
      <div className="max-w-md mx-auto card p-10 text-center">
        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5">
          <CheckCircle size={40} className="text-green-500"/>
        </div>
        <h2 className="font-sora font-black text-acre-dark text-xl mb-2">Pagamento aprovado! 🎉</h2>
        <p className="text-gray-500 text-sm mb-6">Seu acesso está sendo liberado. Aguarde um instante.</p>
        <button onClick={() => navigate('/empresa')}
          className="btn-primary w-full justify-center py-3 flex items-center gap-2">
          <Zap size={15}/> Acessar painel da empresa
        </button>
      </div>
    );
  }

  if (status === 'pending') {
    return (
      <div className="max-w-md mx-auto card p-10 text-center">
        <div className="w-20 h-20 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-5">
          <Clock size={40} className="text-yellow-500"/>
        </div>
        <h2 className="font-sora font-black text-acre-dark text-xl mb-2">Pagamento em processamento</h2>
        <p className="text-gray-500 text-sm mb-3">
          Seu pagamento está sendo processado. O acesso será liberado automaticamente assim que confirmado.
        </p>
        {paymentId && (
          <p className="text-xs text-gray-400 mb-6 font-mono">ID: {paymentId}</p>
        )}
        <div className="space-y-2">
          <button onClick={onVerificar} disabled={verificando}
            className="btn-primary w-full justify-center py-3 flex items-center gap-2 disabled:opacity-60">
            {verificando ? <Loader2 size={15} className="animate-spin"/> : <RefreshCw size={15}/>}
            Verificar status agora
          </button>
          <button onClick={onVoltar} className="btn-outline w-full justify-center py-2.5 text-sm">
            Voltar aos planos
          </button>
        </div>
      </div>
    );
  }

  if (status === 'rejected') {
    return (
      <div className="max-w-md mx-auto card p-10 text-center">
        <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-5">
          <AlertCircle size={40} className="text-red-500"/>
        </div>
        <h2 className="font-sora font-black text-acre-dark text-xl mb-2">Pagamento não aprovado</h2>
        <p className="text-gray-500 text-sm mb-6">
          O pagamento não foi concluído. Tente novamente ou escolha outro método de pagamento.
        </p>
        <button onClick={onVoltar} className="btn-primary w-full justify-center py-3">
          Tentar novamente
        </button>
      </div>
    );
  }

  return null;
}

// ── Componente principal ──────────────────────────────────────
export default function PagamentoEmpresa() {
  const navigate   = useNavigate();
  const [params]   = useSearchParams();

  // Retorno do MercadoPago
  const statusRetorno  = params.get('status');
  const paymentIdRetorno = params.get('payment_id') || params.get('collection_id');
  const [telaRetorno, setTelaRetorno] = useState(!!statusRetorno);
  const [verificando, setVerificando]  = useState(false);

  // Plano selecionado
  const [planoId, setPlanoId] = useState('90_dias');
  const plano = PLANOS.find(p => p.id === planoId);

  // Estado MercadoPago
  const [carregando,   setCarregando]   = useState(false);
  const [preferencia,  setPreferencia]  = useState(null); // { preference_id, init_point, sandbox }

  // Comprovante manual (fallback)
  const [fallback,    setFallback]    = useState(false);
  const [arquivo,     setArquivo]     = useState(null);
  const [enviando,    setEnviando]    = useState(false);
  const [enviado,     setEnviado]     = useState(false);

  // Polling após retorno pendente
  const pollingRef = useRef(null);

  // ── Se voltou do MP com pagamento aprovado, verifica acesso ──
  useEffect(() => {
    if (statusRetorno === 'approved' && paymentIdRetorno) {
      verificarPagamento(paymentIdRetorno);
    }
    if (statusRetorno === 'pending' && paymentIdRetorno) {
      iniciarPolling(paymentIdRetorno);
    }
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, []); // eslint-disable-line

  // ── Verifica status de um pagamento ──────────────────────────
  const verificarPagamento = useCallback(async (pid) => {
    setVerificando(true);
    try {
      const { data } = await api.get(`/empresas/pagamento/mp/status/${pid}`);
      if (data.paid) {
        toast.success('✅ Pagamento confirmado! Redirecionando...');
        setTimeout(() => navigate('/empresa'), 1500);
      } else if (data.status === 'pending' || data.status === 'in_process') {
        toast('⏳ Pagamento ainda em processamento. Aguarde.', { icon: '🕐' });
      } else {
        toast.error('Pagamento não aprovado. Status: ' + data.status);
      }
    } catch {
      toast.error('Erro ao verificar pagamento.');
    }
    setVerificando(false);
  }, [navigate]);

  // ── Polling automático para pagamentos pendentes ──────────────
  const iniciarPolling = useCallback((pid) => {
    pollingRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/empresas/pagamento/mp/status/${pid}`);
        if (data.paid) {
          clearInterval(pollingRef.current);
          toast.success('✅ Pagamento confirmado! Acesso liberado.');
          navigate('/empresa');
        }
      } catch {}
    }, 8000);
  }, [navigate]);

  // ── Criar preferência e redirecionar ─────────────────────────
  const iniciarPagamento = async () => {
    setCarregando(true);
    setPreferencia(null);
    try {
      const { data } = await api.post('/empresas/pagamento/mp', { plano: planoId });
      setPreferencia(data);

      // Redireciona para o checkout do MercadoPago
      const url = data.sandbox ? data.sandbox_init_point : data.init_point;
      if (url) {
        window.location.href = url;
      } else {
        toast.error('Não foi possível obter o link de pagamento.');
      }
    } catch (err) {
      const d = err.response?.data;
      if (d?.fallback) {
        setFallback(true);
        toast('Pagamento online indisponível. Use o comprovante manual.', { icon: '⚠️' });
      } else {
        toast.error(d?.erro || 'Erro ao iniciar pagamento.');
      }
    }
    setCarregando(false);
  };

  // ── Enviar comprovante manual ─────────────────────────────────
  const enviarComprovante = async () => {
    if (!arquivo) return toast.error('Selecione o comprovante.');
    setEnviando(true);
    try {
      const form = new FormData();
      form.append('comprovante', arquivo);
      form.append('plano', planoId);
      form.append('valor', plano.preco);
      form.append('metodo_pagamento', 'pix_manual');
      await api.post('/empresas/pagamento/comprovante', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Comprovante enviado! Aguardando aprovação.');
      setEnviado(true);
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao enviar comprovante.');
    }
    setEnviando(false);
  };

  // ── Tela de comprovante enviado ───────────────────────────────
  if (enviado) {
    return (
      <Layout title="Pagamento" subtitle="Acesso completo aos talentos do Acre">
        <div className="max-w-md mx-auto card p-10 text-center">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5">
            <CheckCircle size={40} className="text-green-500"/>
          </div>
          <h2 className="font-sora font-black text-acre-dark text-xl mb-2">Comprovante enviado!</h2>
          <p className="text-gray-500 text-sm leading-relaxed">
            Analisaremos seu comprovante em até <strong>24 horas úteis</strong>.<br/>
            Você receberá um e-mail quando o acesso for liberado.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Escolha seu Plano" subtitle="Acesso completo aos talentos do Acre">

      {/* Retorno do MercadoPago */}
      {telaRetorno && (
        <TelaRetorno
          status={statusRetorno}
          paymentId={paymentIdRetorno}
          onVoltar={() => setTelaRetorno(false)}
          onVerificar={() => verificarPagamento(paymentIdRetorno)}
          verificando={verificando}
        />
      )}

      {!telaRetorno && (
        <>
          {/* Banner 15 dias grátis */}
          <div className="bg-gradient-to-r from-acre-dark to-[#1a3820] rounded-2xl p-4 mb-6 text-center">
            <p className="text-acre-gold font-bold text-lg">🎁 15 dias GRÁTIS para novas empresas</p>
            <p className="text-white/60 text-xs mt-1">Após o período gratuito, escolha o plano ideal para continuar</p>
          </div>

          {/* Seleção de plano */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {PLANOS.map(p => (
              <button key={p.id} onClick={() => setPlanoId(p.id)}
                className={`relative text-left p-4 rounded-2xl border-2 transition-all active:scale-[0.98] ${
                  planoId === p.id
                    ? 'border-acre-green bg-acre-light shadow-sm'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}>
                {p.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-acre-green text-white text-xs font-black px-3 py-0.5 rounded-full whitespace-nowrap">
                    ⭐ Mais Popular
                  </span>
                )}
                {p.desconto && (
                  <span className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    {p.desconto}
                  </span>
                )}
                <p className={`font-sora font-bold text-base mb-1 ${planoId === p.id ? 'text-acre-green' : 'text-acre-dark'}`}>
                  {p.label}
                </p>
                <p className="font-sora font-black text-2xl text-acre-dark mb-1">
                  R$ {p.preco.toFixed(2).replace('.', ',')}
                </p>
                {p.preco_original && (
                  <p className="text-xs text-gray-400 line-through">
                    R$ {p.preco_original.toFixed(2).replace('.', ',')}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-0.5">{p.descricao}</p>
                {planoId === p.id && (
                  <p className="text-xs text-acre-green font-bold mt-2">✓ Selecionado</p>
                )}
              </button>
            ))}
          </div>

          <div className="max-w-lg space-y-5">

            {/* ── Checkout do plano ── */}
            {!fallback && (
              <div className="card p-6">
                {/* Header */}
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-acre-green/10 flex items-center justify-center flex-shrink-0">
                    <ShoppingCart size={18} className="text-acre-green"/>
                  </div>
                  <div>
                    <p className="font-sora font-bold text-acre-dark">Pagar Plano</p>
                    <p className="text-xs text-gray-400">PIX ou cartão de crédito</p>
                  </div>
                </div>

                {/* Métodos aceitos */}
                <div className="flex flex-wrap gap-2 mb-5">
                  {[
                    { icon: QrCode,      label: 'PIX',       cor: 'bg-green-50 text-green-700 border-green-200' },
                    { icon: CreditCard,  label: 'Crédito',   cor: 'bg-blue-50 text-blue-700 border-blue-200' },
                  ].map(({ icon: Icon, label, cor }) => (
                    <div key={label} className={`flex items-center gap-1.5 text-xs font-semibold border px-2.5 py-1 rounded-full ${cor}`}>
                      <Icon size={11}/> {label}
                    </div>
                  ))}
                </div>

                {/* Resumo do valor */}
                <div className="bg-gray-50 rounded-xl p-4 mb-5">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-600">Plano selecionado</p>
                      <p className="font-sora font-bold text-acre-dark">{plano.label} de acesso</p>
                    </div>
                    <p className="font-sora font-black text-2xl text-acre-dark">
                      R$ {plano.preco.toFixed(2).replace('.', ',')}
                    </p>
                  </div>
                </div>

                {/* Botão principal */}
                <button onClick={iniciarPagamento} disabled={carregando}
                  className="w-full py-4 bg-acre-green hover:bg-acre-mid text-white font-sora font-black rounded-xl flex items-center justify-center gap-3 transition-colors disabled:opacity-60 text-base shadow-sm active:scale-[0.98]">
                  {carregando
                    ? <><Loader2 size={18} className="animate-spin"/> Redirecionando...</>
                    : <><ShoppingCart size={18}/> Pagar Plano</>}
                </button>

                <p className="text-xs text-gray-400 text-center mt-3 flex items-center justify-center gap-1.5">
                  <Shield size={11} className="text-green-500"/>
                  Você será direcionado ao ambiente seguro de pagamento
                </p>

                {/* Selos de segurança */}
                <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-1.5 text-xs text-gray-400">
                    <Shield size={12} className="text-green-500"/>
                    Certificado SSL
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-gray-400">
                    <Lock size={12} className="text-green-500"/>
                    Dados criptografados
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-gray-400">
                    <CheckCircle size={12} className="text-green-500"/>
                    PCI DSS
                  </div>
                </div>
              </div>
            )}

            {/* ── Fallback: PIX manual + comprovante ── */}
            {fallback && (
              <div className="card p-6">
                <div className="flex items-center gap-2 bg-yellow-50 border border-yellow-200 rounded-xl p-3 mb-5">
                  <AlertCircle size={15} className="text-yellow-600 flex-shrink-0"/>
                  <p className="text-xs text-yellow-700">
                    Gateway de pagamento temporariamente indisponível. Realize o pagamento via PIX e envie o comprovante.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 mb-5 space-y-3">
                  <div>
                    <span className="text-xs text-gray-500 font-semibold">Chave PIX (CNPJ)</span>
                    <div className="flex items-center justify-between">
                      <p className="font-mono font-bold text-acre-dark">{PIX_CHAVE}</p>
                      <button onClick={() => { navigator.clipboard.writeText(PIX_CHAVE); toast.success('Chave copiada!'); }}
                        className="text-xs text-acre-green font-bold underline">Copiar</button>
                    </div>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 font-semibold">Beneficiário</span>
                    <p className="text-sm font-semibold text-acre-dark">{PIX_BENEFICIARIO}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 font-semibold">Valor</span>
                    <p className="text-lg font-black text-green-700">
                      R$ {plano.preco.toFixed(2).replace('.', ',')}
                    </p>
                  </div>
                </div>

                {/* Upload comprovante */}
                <div onClick={() => document.getElementById('comp-file').click()}
                  className={`border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition-all mb-4 ${
                    arquivo ? 'border-acre-green bg-acre-light' : 'border-gray-200 hover:border-acre-green hover:bg-gray-50'
                  }`}>
                  <input type="file" id="comp-file" accept=".jpg,.jpeg,.png,.pdf"
                    className="hidden" onChange={e => setArquivo(e.target.files[0])}/>
                  <Upload size={24} className={`mx-auto mb-2 ${arquivo ? 'text-acre-green' : 'text-gray-400'}`}/>
                  <p className="text-sm font-semibold text-gray-600">
                    {arquivo ? arquivo.name : 'Clique para enviar o comprovante'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">JPG, PNG ou PDF · máx. 10MB</p>
                </div>

                <button onClick={enviarComprovante} disabled={!arquivo || enviando}
                  className="w-full py-3.5 bg-acre-green hover:bg-acre-mid text-white font-black rounded-xl disabled:opacity-60 transition-all">
                  {enviando ? 'Enviando...' : 'Enviar Comprovante'}
                </button>

                <button onClick={() => setFallback(false)}
                  className="w-full mt-2 text-sm text-acre-green hover:underline py-2">
                  ← Voltar ao pagamento online
                </button>
              </div>
            )}

            {/* Info benefícios */}
            <div className="card p-5 bg-gradient-to-br from-acre-light/50 to-white">
              <p className="font-sora font-bold text-sm text-acre-dark mb-3">
                ✅ O que você tem acesso com o plano:
              </p>
              <div className="space-y-2">
                {[
                  'Candidatos compatíveis via Algoritmo de Match IA',
                  'Chat direto com candidatos com score ≥ 80%',
                  'Publicação ilimitada de vagas',
                  'Painel completo de seleção e entrevistas',
                  'Contato via WhatsApp com candidatos',
                  `Acesso por ${plano.dias} dias corridos`,
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                    <CheckCircle size={14} className="text-acre-green flex-shrink-0"/>
                    {item}
                  </div>
                ))}
              </div>
            </div>

          </div>
        </>
      )}
    </Layout>
  );
}
